import React, {useEffect, useState} from 'react'
import Swal from 'sweetalert2'
import AddFarmerform from './AddFarmerform'
import {addFarmer, getEnterpriseName, getFarms} from '../../../api'
import {KTSVG} from '../../../_metronic/helpers'
import countries from '../../../utils/constants'

interface FarmerResponse {
  email: string
  name: string
  source: string
  country_code: number
  ph: string
  unit: string
  id: number
  apikey: string
  password: string
  company_id: number
  company_name: string
}
function FarmerModal({isOpen, onClose}) {
  const [formData, setFormData] = useState<{
    name: string
    email: string
    ph: string
    company_id: string
    country_code: string
    source: string
    unit: string
    company_name: string
    password?: string
  }>({
    name: '',
    email: '',
    ph: '',
    company_id: '',
    country_code: '',
    source: '',
    unit: 'metric',
    company_name: '',
  })

  const [submittedForm, setSubmittedForm] = useState(false)
  // const [password, setPassword] = useState({
  //     password: ''
  // })
  const [farmerResponse, setFarmerResponse] = useState<FarmerResponse>()
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [enterpriseName, setEnterpriseName] = useState([])
  const [farms, setFarms] = useState([])
  const [farmsLoading, setFarmsLoading] = useState(false)

  useEffect(() => {
    const fetchEnterprises = async () => {
      try {
        const response = await getEnterpriseName()
        setEnterpriseName(response)
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to fetch enterprises. Please try again later.',
        })
      }
    }

    fetchEnterprises()
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target
    setFormData((prev) => ({...prev, [name]: value}))
  }

  const handleSelectChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }))

    // Fetch farms when enterprise is selected
    if (field === 'company_id' && value) {
      fetchFarms(value)
    }
  }

  const fetchFarms = async (enterpriseId: string | number) => {
    setFarmsLoading(true)
    try {
      const response = await getFarms({
        page: 1,
        limit: 10,
        sort_by: 'created_at',
        sort_order: 'desc',
      })
      setFarms(response.farms || [])
    } catch (error) {
      console.error('Error fetching farms:', error)
      Swal.fire({
        icon: 'error',
        title: 'Failed to fetch farms',
        text: 'Could not retrieve farm data for this enterprise.',
      })
      setFarms([])
    } finally {
      setFarmsLoading(false)
    }
  }

  // const handleFileUpload = () => {
  //     const fileInput = document.getElementById('fileUploadInput') as HTMLInputElement | null;
  //     fileInput?.click();
  // };

  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const uploadedFile = e.target.files?.[0];
  //     if (uploadedFile) {
  //         const fileExtension = uploadedFile.name.split('.').pop()?.toLowerCase();
  //         if (fileExtension === 'csv') {
  //             setFile(uploadedFile);
  //             Swal.fire({
  //                 icon: 'success',
  //                 title: 'File Uploaded',
  //                 text: `The file "${uploadedFile.name}" has been uploaded successfully.`,
  //             });
  //         } else {
  //             Swal.fire({
  //                 icon: 'error',
  //                 title: 'Invalid File',
  //                 text: 'Only CSV files are allowed. Please try again.',
  //             });
  //             e.target.value = '';
  //         }
  //     }
  // };

  const validateForm = () => {
    if (!formData.name) {
      return 'Farmer Name is required.'
    }
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      return 'Please enter a valid email address.'
    }

    const selectedCountry = countries.find((country) => country.code === formData.country_code)
    if (!selectedCountry) return 'Please select a valid country.'

    if (
      !formData.ph ||
      !/^\d+$/.test(formData.ph) ||
      formData.ph.length !== (selectedCountry?.phoneNumberLength || 0)
    ) {
      return `Phone number must be a valid ${selectedCountry?.phoneNumberLength}-digit number for ${selectedCountry?.name}.`
    }
    return null
  }
  const handleSubmit = async () => {
    const errorMessage = validateForm();
    if (errorMessage) {
      Swal.fire({
        icon: 'error',
        title: errorMessage,
      });
      return;
    }
  
    setLoading(true);
  
    try {
      Swal.fire({
        title: 'Submitting...',
        text: 'Please wait while the farmer is being added.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });
  
      // Create the payload dynamically
      const { email, ...restFormData } = formData;
      const requestData = email ? { ...formData } : { ...restFormData }; // Exclude email if empty
  
      const response = await addFarmer(requestData);
      setFarmerResponse(response);
  
      Swal.fire({
        icon: 'success',
        title: `Account is created for ${formData.name}.`,
      }).then(() => {
        setSubmittedForm(true);
      });
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: error.response?.data?.detail || 'Failed to create farmer.',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const resetData = () => {
    setFormData({
      name: '',
      email: '',
      ph: '',
      company_id: '',
      country_code: '',
      source: '',
      unit: 'metric',
      company_name: '',
    })
    setFarms([])
    onClose()
    setSubmittedForm(false)
  }

  const downloadCSV = () => {
    if (!farmerResponse) {
      return
    }

    const data = [
      [
        'Farmer Name',
        'Email',
        'Country Code',
        'Phone',
        'Unit',
        'Source',
        'Password',
        'Enterprise Name',
        'Enterprise Id',
      ],
      [
        farmerResponse.name,
        farmerResponse.email,
        farmerResponse.country_code,
        farmerResponse.ph,
        farmerResponse.unit,
        farmerResponse.source,
        farmerResponse.password,
        farmerResponse.company_id,
        farmerResponse.company_name,
        // farmerResponse.enterpriseName,
      ],
    ]

    const csvContent = data.map((e) => e.join(',')).join('\n')

    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', 'farmer_response.csv')
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div
      className={`modal fade ${isOpen ? 'show' : ''}`}
      tabIndex={-1}
      id='kt_modal_1'
      style={{display: isOpen ? 'block' : 'none'}}
    >
      <div className='modal-dialog'>
        <div className='modal-content' style={{marginTop: '10rem'}}>
          <div className='modal-header'>
            {submittedForm ? (
              <h3 className='modal-title'>Farmer Created Successfully</h3>
            ) : (
              <>
                <h3 className='modal-title'>Create New Farmer</h3>
                <div
                  className='btn btn-icon btn-sm btn-active-secondary ms-2'
                  data-bs-dismiss='modal'
                  aria-label='Close'
                  onClick={resetData}
                >
                  <KTSVG
                    path='media/icons/duotune/arrows/arr061.svg'
                    className='svg-icon svg-icon-2x'
                  />
                </div>
                {/* <button
                                    type="button"
                                    className="btn"
                                    style={{ backgroundColor: '#6D9300', padding: '5px', color: 'white' }}
                                    onClick={handleFileUpload}
                                >
                                    <i className="ki-solid ki-file-up" style={{ color: 'white' }}></i> Bulk Upload
                                </button> */}
              </>
            )}
          </div>
          <div className='modal-body' style={{height: '35vh', overflow: 'auto'}}>
            <AddFarmerform
              formData={formData}
              onInputChange={handleInputChange}
              onSelectChange={handleSelectChange}
              submittedForm={submittedForm}
              // password={password}
              farmerResponse={farmerResponse}
              // enterprises={enterpriseName}
              enterpriseName={enterpriseName}
            />
          </div>
          {/* Farms Table Section */}
          {formData.company_id && (
            <div className='modal-body' style={{height: 'auto', maxHeight: '40vh', overflow: 'auto', borderTop: '1px solid #e0e0e0', marginTop: '1rem'}}>
              <h5 className='mb-3'>Farms Data</h5>
              {farmsLoading ? (
                <div className='text-center py-3'>
                  <div className='spinner-border spinner-border-sm' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </div>
                </div>
              ) : farms.length > 0 ? (
                <div className='table-responsive'>
                  <table className='table table-sm table-striped'>
                    <thead className='table-light'>
                      <tr>
                        <th>Farm Name</th>
                        <th>Farmer Name</th>
                        <th>Area</th>
                        <th>Status</th>
                        <th>Created Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {farms.map((farm: any) => (
                        <tr key={farm.id}>
                          <td className='text-truncate' title={farm.name}>{farm.name}</td>
                          <td className='text-truncate' title={farm.farmer_name}>{farm.farmer_name}</td>
                          <td>{farm.area.toFixed(2)} ha</td>
                          <td>
                            <span className={`badge ${farm.is_paid ? 'bg-success' : 'bg-warning'}`}>
                              {farm.is_paid ? 'Paid' : 'Free'}
                            </span>
                          </td>
                          <td>{new Date(farm.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className='text-muted text-center py-3'>No farms found for this enterprise.</p>
              )}
            </div>
          )}
          <div className='modal-footer'>
            {submittedForm ? (
              <div className='d-flex justify-content-between w-100'>
                <button
                  type='button'
                  className='btn'
                  style={{backgroundColor: '#6D9300', color: 'white'}}
                  onClick={downloadCSV}
                >
                  Download CSV
                </button>
                <button
                  type='button'
                  className='btn btn-secondary'
                  data-bs-dismiss='modal'
                  onClick={resetData}
                >
                  Close
                </button>
              </div>
            ) : (
              <button
                type='button'
                className='btn'
                style={{backgroundColor: '#6D9300', color: 'white'}}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Create Farmer'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* <input
                type="file"
                id="fileUploadInput"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            /> */}
    </div>
  )
}

export default FarmerModal
